/**
 * The onboarding vocabulary, computed from the journey declarations.
 *
 * Every export here replaces a literal that used to be typed out by hand in
 * `model.ts`, `transitions.ts`, `onboarding-stages.ts` or the admin
 * advance-step route, each of which had to agree with the others by review
 * alone. Names and values are unchanged — only their authorship moved, so a
 * rung is now added or retired in exactly one place.
 *
 * Pure and client-safe: no database, auth, or side-effectful imports.
 */

import {
  ALL_JOURNEYS,
  BUILDER_STANDARD,
  SUBCONTRACTOR_STANDARD,
  type JourneyStage,
} from './journeys';

// ─── Vocabulary types ────────────────────────────────────────────────────────

type BuilderStage = (typeof BUILDER_STANDARD)[number];
type SubStage = (typeof SUBCONTRACTOR_STANDARD)[number];
type AnyStage = BuilderStage | SubStage;

type StatusOf<S> = S extends { legacy: { statusValue: infer V extends string } } ? V : never;
type StepOf<S> = S extends { legacy: { userStep: infer V extends string } } ? V : never;
type AliasOf<S> = S extends { legacy: { aliases: infer A extends readonly string[] } }
  ? A[number]
  : never;

export type BuilderStatus = StatusOf<BuilderStage>;
export type SubStatus = StatusOf<SubStage>;
export type UserStep = StepOf<AnyStage>;
/** A user step as it may arrive on a read — canonical, or a historic alias. */
export type OnboardingStep = UserStep | AliasOf<AnyStage>;

// The `as` casts below all carry one fact a runtime loop cannot express: the
// values collected off `legacy` are exactly the union the type-level
// derivation above extracted from the same declaration. Nothing else is
// asserted, and the parity test pins the values.

// ─── Entity statuses (mirror the DB CHECK constraints exactly) ───────────────

function statusValuesOf(journey: readonly JourneyStage[]): readonly string[] {
  const out: string[] = [];
  for (const stage of journey) {
    if (stage.legacy.statusValue !== undefined) out.push(stage.legacy.statusValue);
  }
  return out;
}

export const BUILDER_STATUSES = statusValuesOf(BUILDER_STANDARD) as readonly BuilderStatus[];
export const SUB_STATUSES = statusValuesOf(SUBCONTRACTOR_STANDARD) as readonly SubStatus[];

// ─── User steps ──────────────────────────────────────────────────────────────

/** Steps whose stage satisfies `pred`, across both journeys in journey order;
 *  a step both personas walk is listed once, where the first journey puts it. */
function stepsWhere(pred: (stage: JourneyStage) => boolean): readonly string[] {
  const out: string[] = [];
  for (const journey of ALL_JOURNEYS) {
    for (const stage of journey) {
      const step = stage.legacy.userStep;
      if (step !== undefined && pred(stage) && !out.includes(step)) out.push(step);
    }
  }
  return out;
}

export const USER_STEPS = stepsWhere(() => true) as readonly UserStep[];

/** One writable step, with the label the declaration gives its stage. The
 *  admin surfaces that let a specialist move someone by hand render this
 *  instead of keeping their own copy of the list — a rung retired from the
 *  journey disappears from the pickers in the same edit, rather than staying
 *  offerable until someone notices the write is rejected. */
export interface UserStepOption {
  value: UserStep;
  label: string;
}

function buildUserStepOptions(): readonly UserStepOption[] {
  const labels = new Map<string, string>();
  for (const journey of ALL_JOURNEYS) {
    for (const stage of journey) {
      const step = stage.legacy.userStep;
      if (step !== undefined && !labels.has(step)) labels.set(step, stage.label);
    }
  }
  return USER_STEPS.map((value) => ({ value, label: labels.get(value) as string }));
}

export const USER_STEP_OPTIONS = buildUserStepOptions();

/** Forward ordering of user steps (higher = further along). An alias shares
 *  its stage's ordinal, so a row stored under the old spelling compares
 *  identically to one stored under the new. */
function buildStepOrdinal(): Readonly<Record<string, number>> {
  const ordinal: Record<string, number> = {};
  USER_STEPS.forEach((step, i) => {
    ordinal[step] = i;
  });
  for (const journey of ALL_JOURNEYS) {
    for (const stage of journey) {
      const step = stage.legacy.userStep;
      if (step === undefined) continue;
      for (const alias of stage.legacy.aliases ?? []) ordinal[alias] = ordinal[step];
    }
  }
  return ordinal;
}

export const STEP_ORDINAL = buildStepOrdinal();

// ─── Labels ──────────────────────────────────────────────────────────────────

/** Human-facing label for every entity status across both personas. Personas
 *  that share a status share its label; the first journey to declare one wins,
 *  and the parity test pins the result. */
function buildStatusLabels(): Readonly<Record<string, string>> {
  const labels: Record<string, string> = {};
  for (const journey of ALL_JOURNEYS) {
    for (const stage of journey) {
      const status = stage.legacy.statusValue;
      if (status !== undefined && labels[status] === undefined) labels[status] = stage.label;
    }
  }
  return labels;
}

export const STATUS_LABELS = buildStatusLabels();

// ─── Lanes ───────────────────────────────────────────────────────────────────

/** The personal attestations. Account-level progress may neither imply nor
 *  skip one, so the reconciler leaves a user sitting here alone. */
export const USER_LANE_STEPS: ReadonlySet<string> = new Set(
  stepsWhere((stage) => stage.lane === 'user')
);

/** The steps an entity fan-out may advance FROM: everything that is a company
 *  fact. The attestations' absence is not an exception maintained here — it
 *  falls out of their lane. */
export const ENTITY_LANE_USER_STEPS = stepsWhere(
  (stage) => stage.lane === 'entity'
) as readonly UserStep[];

// ─── Status → step floors ────────────────────────────────────────────────────

/**
 * The step an entity status implies for its users. Only company facts appear:
 * a stage needs an entity status to be implied BY and a user step to be
 * implied AS, and the attestations are excluded by lane rather than by name.
 */
function stepFloors(journey: readonly JourneyStage[]): Readonly<Record<string, UserStep>> {
  const floors: Record<string, string> = {};
  for (const stage of journey) {
    if (stage.lane !== 'entity') continue;
    const { statusValue, userStep } = stage.legacy;
    if (statusValue !== undefined && userStep !== undefined) floors[statusValue] = userStep;
  }
  return floors as Record<string, UserStep>;
}

export const BUILDER_STATUS_STEP_FLOOR = stepFloors(BUILDER_STANDARD);
export const SUBCONTRACTOR_STATUS_STEP_FLOOR = stepFloors(SUBCONTRACTOR_STANDARD);

// ─── Portal routing ──────────────────────────────────────────────────────────

/**
 * Steps at which the user has cleared the guided, page-pinned rungs and may
 * navigate the portal freely — exactly the stages declared `roam`.
 *
 * Read off the declaration rather than compared against an ordinal, so a rung
 * that roams does not have to be preceded only by rungs that also do.
 */
export const PORTAL_FREE_NAV_STEPS: ReadonlySet<string> = new Set(
  stepsWhere((stage) => stage.roam)
);

export interface JourneyRouting {
  /** Step (or alias) → the page a user on it belongs on. A Map, not a record,
   *  so a stored step that collides with an Object prototype key can never
   *  resolve to something that is not a route. */
  readonly byStep: ReadonlyMap<string, string>;
  /** Where an unrecognised step lands — including a step the OTHER persona's
   *  journey has and this one does not. */
  readonly home: string;
}

function routingFor(journey: readonly JourneyStage[]): JourneyRouting {
  const byStep = new Map<string, string>();
  for (const stage of journey) {
    const { userStep, aliases } = stage.legacy;
    if (stage.route === undefined || userStep === undefined) continue;
    byStep.set(userStep, stage.route);
    for (const alias of aliases ?? []) byStep.set(alias, stage.route);
  }
  // Home is the last routable rung: where a finished user lives, and the only
  // sane landing for a step this journey does not know. Fail closed rather
  // than hand callers an undefined URL.
  const home = [...journey].reverse().find((s) => s.route !== undefined)?.route;
  if (home === undefined) throw new Error('Onboarding journey declares no routable stage');
  return { byStep, home };
}

export const BUILDER_ROUTING = routingFor(BUILDER_STANDARD);
export const SUBCONTRACTOR_ROUTING = routingFor(SUBCONTRACTOR_STANDARD);

/**
 * The pages the guided onboarding flow owns — every declared route under
 * `/onboarding`, across both journeys, listed once.
 *
 * The portal's own pages are excluded by the prefix rather than by name: a rung
 * that routes into the portal (the document phases, the dashboards) is somewhere
 * the user navigates as a portal user, not a page the onboarding shell wraps.
 */
export const ONBOARDING_ROUTES: readonly string[] = [
  ...new Set(
    ALL_JOURNEYS.flatMap((journey) =>
      journey
        .map((stage) => stage.route)
        .filter((route): route is string => route !== undefined && route.startsWith('/onboarding')),
    ),
  ),
];

// ─── Admin stepper ───────────────────────────────────────────────────────────

/** One rung of the admin stepper. Shaped here rather than imported from
 *  `@/lib/onboarding-stages` so the vocabulary stays free of its consumers. */
export interface StepperStage<K extends string> {
  key: K;
  label: string;
  description: string;
  isAdminJumpable: boolean;
  jumpStep?: string;
}

function stepperStagesOf<K extends string>(journey: readonly JourneyStage[]): StepperStage<K>[] {
  const stages: StepperStage<K>[] = [];
  for (const stage of journey) {
    const status = stage.legacy.statusValue;
    if (status === undefined || stage.stepper === undefined) continue;
    const { label, description, adminJumpStep } = stage.stepper;
    stages.push({
      key: status as K,
      label,
      description,
      isAdminJumpable: adminJumpStep !== undefined,
      ...(adminJumpStep !== undefined ? { jumpStep: adminJumpStep } : {}),
    });
  }
  return stages;
}

export const BUILDER_STAGES = stepperStagesOf<BuilderStatus>(BUILDER_STANDARD);
export const SUBCONTRACTOR_STAGES = stepperStagesOf<SubStatus>(SUBCONTRACTOR_STANDARD);

// ─── Admin step targets ──────────────────────────────────────────────────────

/**
 * Builder step → the `onboarding_status` an admin jump to it writes.
 *
 * Only rungs that record BOTH appear: a jump names a step, and what it writes
 * is that rung's own company status. A rung that is a user position only — a
 * branch the company status does not track — is not a jump target, because
 * there is no status to move the company to.
 */
function stepToStatus(journey: readonly JourneyStage[]): Readonly<Record<string, string>> {
  const map: Record<string, string> = {};
  for (const stage of journey) {
    const { statusValue, userStep } = stage.legacy;
    if (statusValue !== undefined && userStep !== undefined) map[userStep] = statusValue;
  }
  return map;
}

export const STEP_TO_STATUS = stepToStatus(BUILDER_STANDARD);

/**
 * Canonical forward ordering of builder statuses for the advance-step
 * backward-move check: exactly the statuses a step can name, in journey order.
 *
 * The origin drops out for free — no step names it, so an admin can never
 * "advance to" it, and including it would shift every index and break the
 * comparison. The stepper still renders it as a leading marker.
 */
export const BUILDER_STATUS_SEQUENCE: readonly string[] = [
  ...new Set(Object.values(STEP_TO_STATUS)),
];
